import type { ButtonHTMLAttributes, ReactNode } from 'react'

type Kind = 'primary' | 'secondary' | 'tertiary'

const KIND_CLASS: Record<Kind, string> = {
  primary: 'btn btn-primary',
  secondary: 'btn',
  tertiary: 'btn btn-ghost',
}

/**
 * Wraps the .btn classes already in tokens.css rather than restyling: those values came
 * from the handoff and the marketing page already renders with them.
 *
 * type defaults to "button". A native button inside a <form> defaults to submit, which
 * silently submits when someone means "cancel" — a real bug the Vibe wrapper hid.
 */
export function Button({
  kind = 'secondary',
  size,
  type = 'button',
  children,
  className,
  ...rest
}: {
  readonly kind?: Kind
  readonly size?: 'small'
  readonly children: ReactNode
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  const { style, disabled, ...buttonProps } = rest
  const classes = [KIND_CLASS[kind], size === 'small' ? 'btn-sm' : '', className ?? '']
    .filter((c) => c !== '')
    .join(' ')
  return (
    <button
      type={type}
      className={classes}
      aria-disabled={disabled || undefined}
      style={{ ...style, ...(disabled === true ? { pointerEvents: 'none' as const } : {}) }}
      disabled={disabled}
      {...buttonProps}
    >
      {children}
    </button>
  )
}
